/**
 * Eén manier van zoektermen vergelijken, gedeeld door de zoekbalk en de zoekresultatenpagina.
 *
 * Waarom. Beide plekken deden `tekst.includes(term)` op hun eigen manier, en dat laat twee soorten
 * treffers liggen:
 *
 * 1. **Groepen.** "kit" vond wel een Kitmes en een kitpistool, maar niet de groep *Kitten* met de
 *    63 siliconen-, acrylaat- en beglazingskitten. Die groepen staan nu in de index
 *    (getGroepPaginas) — dit bestand zorgt dat ze ook gevonden wórden.
 * 2. **Nederlandse meervouden met een lange klinker.** "boor" matcht niet op "Boren" en "zaag" niet
 *    op "Zagen": in het meervoud valt de dubbele klinker weg (open lettergreep). Puur op
 *    tekstvergelijking zijn dat vreemden. Daarom vergelijken we óók op een STAM waarin dubbele
 *    klinkers zijn ingekort: boor → bor, en "boren" begint met "bor". Zelfde truc dekt glaas/glazen,
 *    raam/ramen, zuiger/zuigers.
 *
 * Een stam-treffer telt bewust LICHTER dan een echte tekstoverlap (zie `termScore`), zodat wie op
 * "kit" zoekt nog steeds eerst de kitten ziet en niet iets wat alleen via de stam meelift.
 */

/** Kleine letters, zonder accenten. */
export function normaliseerZoek(s: string): string {
  return String(s ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

/**
 * Stam met ingekorte dubbele klinkers: "boor" → "bor", "glaszuiger" → "glaszuiger".
 * Bewust alleen de vier Nederlandse verdubbelingen; "ie" en "eu" zijn losse klanken en blijven.
 */
export function zoekStam(s: string): string {
  return normaliseerZoek(s).replace(/aa|ee|oo|uu/g, (m) => m.charAt(0));
}

/** Score van één zoekwoord tegen een stuk tekst; 0 = geen treffer. */
export function termScore(tekst: string, woord: string): number {
  const hooi = normaliseerZoek(tekst);
  const naald = normaliseerZoek(woord);
  if (!naald) return 0;
  if (hooi.startsWith(naald)) return 3;
  // Woordbegin binnen de tekst ("Halfopen handkitpistool" op "kit") telt zwaarder dan zomaar ergens.
  if (new RegExp(`\\b${naald.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`).test(hooi)) return 2;
  if (hooi.includes(naald)) return 1.5;
  // Laatste kans: via de stam, zodat "boor" de groep "Boren" vindt.
  return zoekStam(hooi).includes(zoekStam(naald)) ? 0.5 : 0;
}

/** Matcht elk woord van de zoekterm ergens in de tekst? (Alle woorden moeten raak zijn.) */
export function alleWoordenRaak(tekst: string, query: string): boolean {
  const woorden = normaliseerZoek(query).trim().split(/\s+/).filter(Boolean);
  if (!woorden.length) return false;
  return woorden.every((w) => termScore(tekst, w) > 0);
}
